import { useCallback, useEffect, useState } from "react";
import { Eye, MoreHorizontal, Pencil, Search, SlidersHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MemberDetailModal } from "@/components/MemberDetailModal";
import { ClientActionBar } from "@/components/admin/ClientActionBar";
import { AddProgressModal } from "@/components/admin/AddProgressModal";

const PAGE_SIZE = 25;

export default function AdminMembers() {
  const [result, setResult] = useState(null); // {total, page, page_size, members}
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [broker, setBroker] = useState("");
  const [ibId, setIbId] = useState("");
  const [notChecked, setNotChecked] = useState(false);
  const [remarkFilter, setRemarkFilter] = useState("");
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [ibOptions, setIbOptions] = useState([]);

  const [busyId, setBusyId] = useState(null);
  const [manage, setManage] = useState(null);
  const [modalError, setModalError] = useState("");
  const [modalBusy, setModalBusy] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [purgeModal, setPurgeModal] = useState(null);
  const [addProgressMember, setAddProgressMember] = useState(null);
  const [uncheckingAll, setUncheckingAll] = useState(false);

  const loadMembers = useCallback((page = 1, q = search, b = broker, ib = ibId, nc = notChecked, rm = remarkFilter) => {
    setLoading(true);
    api
      .get("/admin/members/search", {
        params: { q: q || undefined, broker: b || undefined, ib_id: ib || undefined, not_checked: nc || undefined, remark: rm || undefined, page, page_size: PAGE_SIZE },
      })
      .then((res) => setResult(res.data))
      .catch((err) => { toast.error(formatApiError(err)); setResult({ total: 0, page: 1, page_size: PAGE_SIZE, members: [] }); })
      .finally(() => setLoading(false));
  }, [search, broker, ibId, notChecked, remarkFilter]);

  useEffect(() => { loadMembers(1, "", "", "", false, ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/admin/broker-catalog").then((res) => setBrokerOptions(res.data)).catch(() => setBrokerOptions([]));
    api.get("/admin/ibs").then((res) => setIbOptions(res.data)).catch(() => setIbOptions([]));
  }, []);

  const onBroker = (v) => { setBroker(v); loadMembers(1, search, v, ibId, notChecked, remarkFilter); };
  const onIb = (v) => { setIbId(v); loadMembers(1, search, broker, v, notChecked, remarkFilter); };
  const onNotChecked = (v) => { setNotChecked(v); loadMembers(1, search, broker, ibId, v, remarkFilter); };
  const onRemark = (v) => { setRemarkFilter(v); loadMembers(1, search, broker, ibId, notChecked, v); };
  const handleSearch = (e) => { e.preventDefault(); loadMembers(1, search, broker, ibId, notChecked, remarkFilter); };
  const gotoPage = (p) => loadMembers(p, search, broker, ibId, notChecked, remarkFilter);

  const members = result?.members ?? null;
  const total = result?.total ?? 0;
  const page = result?.page ?? 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const patchMember = (id, partial) =>
    setResult((r) => (r ? { ...r, members: r.members.map((m) => (m.id === id ? { ...m, ...partial } : m)) } : r));

  const uncheckAllEmailFound = async () => {
    if (!window.confirm("Uncheck EMAIL FOUND for all members? Admin remarks and other member data will remain unchanged.")) return;
    setUncheckingAll(true);
    try {
      const { data } = await api.post("/admin/members/email-check/uncheck-all");
      toast.success(`EMAIL FOUND reset for ${data.reset} member${data.reset === 1 ? "" : "s"}`);
      loadMembers(page, search, broker, ibId, notChecked, remarkFilter);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUncheckingAll(false);
    }
  };

  const toggleStatus = async (member) => {
    const next = member.status === "active" ? "suspended" : "active";
    setBusyId(member.id);
    try {
      await api.patch(`/admin/users/${member.id}/status`, { status: next });
      patchMember(member.id, { status: next });
      toast.success(`${member.full_name} is now ${next}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusyId(null); }
  };

  const openPurgeMember = async (member) => {
    setPurgeModal({ member, preview: null, confirm: "", busy: false });
    try {
      const { data } = await api.post(`/admin/members/${member.id}/purge-preview`);
      setPurgeModal((m) => ({ ...m, preview: data }));
    } catch (err) { toast.error(formatApiError(err)); setPurgeModal(null); }
  };
  const executeMemberPurge = async () => {
    setPurgeModal((m) => ({ ...m, busy: true }));
    try {
      await api.post(`/admin/members/${purgeModal.member.id}/purge`, { confirmation: purgeModal.confirm });
      toast.success(`${purgeModal.member.full_name} permanently deleted`);
      setPurgeModal(null);
      loadMembers(page, search, broker, ibId);
    } catch (err) { setPurgeModal((m) => ({ ...m, busy: false })); toast.error(formatApiError(err)); }
  };

  const saveEditMember = async () => {
    setModalBusy(true); setModalError("");
    try {
      const { data } = await api.put(`/admin/members/${editModal.id}`, {
        full_name: editModal.full_name, email: editModal.email, username: editModal.username,
        phone: editModal.phone, country: editModal.country,
      });
      patchMember(editModal.id, data);
      toast.success("Member updated");
      setEditModal(null);
    } catch (err) { setModalError(formatApiError(err)); } finally { setModalBusy(false); }
  };

  const openManage = async (member) => {
    setModalError("");
    setManage({ member, loading: true, info: null, ibQuery: "", ibPick: null, brokerPick: "", msg: null });
    try {
      const [info, ibs, brokers] = await Promise.all([
        api.get(`/admin/members/${member.id}/sponsorship`), api.get("/admin/ibs"), api.get("/brokers"),
      ]);
      setManage((m) => m && { ...m, loading: false, info: info.data, ibs: ibs.data || [], brokers: brokers.data || [], brokerPick: info.data.registered_broker || "" });
    } catch (err) { setManage((m) => m && { ...m, loading: false }); setModalError(formatApiError(err)); }
  };
  const applySponsorship = async (payload, label) => {
    const { member, info } = manage;
    setModalBusy(true); setModalError("");
    try {
      const { data } = await api.patch(`/admin/members/${member.id}/sponsorship`, { ...payload, expected_updated_at: info.updated_at });
      if (!data.changed) { setManage((m) => ({ ...m, msg: `${label}: no change (same value)` })); return; }
      if (data.member) patchMember(member.id, data.member);
      const fresh = await api.get(`/admin/members/${member.id}/sponsorship`);
      setManage((m) => ({ ...m, info: fresh.data, ibPick: null, ibQuery: "", brokerPick: fresh.data.registered_broker || "", msg: `${label} applied`, member: data.member ? { ...m.member, ...data.member } : m.member }));
      toast.success(`${member.full_name}: ${label.toLowerCase()} applied`);
    } catch (err) { setModalError(formatApiError(err)); } finally { setModalBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-members-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="eyebrow-label mb-2">Management</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Members</h1>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            className="input-lux flex-1 sm:w-72" placeholder="Search UID, name or email..." data-testid="admin-member-search-input" />
          <button type="submit" className="btn-outline-gold px-4 py-2 text-sm flex items-center gap-2" data-testid="admin-member-search-button">
            <Search className="h-4 w-4" /> Search
          </button>
        </form>
      </div>

      {/* Unified filters: broker + sponsoring IB, combined with search on the backend */}
      <div className="card-lux p-4 sm:p-5 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="members-filters">
        <div className="flex-1">
          <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-2">Broker</p>
          <select value={broker} onChange={(e) => onBroker(e.target.value)} className="input-lux w-full" data-testid="members-filter-broker">
            <option value="">All Brokers</option>
            {brokerOptions.map((b) => (
              <option key={b.id} value={b.name}>{b.name}{b.status === "inactive" ? " (inactive)" : ""}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-2">Sponsoring IB</p>
          <select value={ibId} onChange={(e) => onIb(e.target.value)} className="input-lux w-full" data-testid="members-filter-ib">
            <option value="">All IBs</option>
            {ibOptions.map((ib) => (
              <option key={ib.id} value={ib.id}>{ib.full_name}{ib.referral_code ? ` (${ib.referral_code})` : ""}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-2">Remark</p>
          <select value={remarkFilter} onChange={(e) => onRemark(e.target.value)} className="input-lux w-full" data-testid="members-filter-remark">
            <option value="">All remarks</option>
            <option value="IB CHANGED">IB CHANGED</option>
            <option value="MIB - LAN">MIB - LAN</option>
            <option value="MIB - MIRA">MIB - MIRA</option>
          </select>
        </div>
        <p className="text-sm text-[#D4AF37] shrink-0 pb-2.5" data-testid="members-total">
          {loading ? "Loading…" : `${total} member${total === 1 ? "" : "s"}`}
        </p>
        <div className="flex items-center gap-2 shrink-0 pb-1">
          <button onClick={uncheckAllEmailFound} disabled={uncheckingAll} data-testid="members-uncheck-all"
            className="text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border border-[#222530] text-[#94A3B8] hover:border-[#D4AF37]/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {uncheckingAll ? "Unchecking…" : "Uncheck All"}
          </button>
          <button onClick={() => onNotChecked(false)} data-testid="members-filter-all"
            className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${!notChecked ? "text-[#0B0C10] bg-[#D4AF37] border-[#D4AF37]" : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/40"}`}>All</button>
          <button onClick={() => onNotChecked(true)} data-testid="members-filter-notchecked"
            className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${notChecked ? "text-[#0B0C10] bg-[#D4AF37] border-[#D4AF37]" : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/40"}`}>Not Checked</button>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-members-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">UID</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Sponsor IB</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Rank</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-5 py-4 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {members === null ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-[#64748B]">Loading members...</td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-[#64748B]" data-testid="admin-members-empty">No members found.</td></tr>
              ) : (
                members.map((m) => (
                  <tr key={m.id} className={`transition-colors align-top ${m.email_found ? "bg-[#D4AF37]/[0.07] hover:bg-[#D4AF37]/[0.1]" : "hover:bg-[#0B0C10]/40"}`} data-testid={`admin-member-row-${m.username}`} data-checked={m.email_found ? "true" : "false"}>
                    <td className="px-5 py-4 font-mono text-[#D4AF37] text-xs" data-testid={`admin-member-uid-${m.username}`}>{m.uid || "—"}</td>
                    <td className="px-5 py-4">
                      <p className="text-white font-medium">{m.full_name}</p>
                      <p className="text-xs text-[#64748B] font-mono mb-3">{m.username} · {m.email}</p>
                      <ClientActionBar
                        member={m}
                        ibId={m.sponsor_ib_user_id}
                        onChanged={(partial) => patchMember(m.id, partial)}
                        onAddProgress={() => setAddProgressMember(m)}
                      />
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <p className="text-[#94A3B8] text-xs">{m.sponsor_name || "—"}</p>
                      <p className="font-mono text-[#D4AF37] text-xs">{m.sponsor_code || ""}</p>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell"><span className="text-xs font-medium text-[#D4AF37]">{m.rank?.name || "—"}</span></td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        m.status === "active" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" : "text-red-400 bg-red-400/10 border-red-400/30"
                      }`} data-testid={`admin-member-status-${m.username}`}>{m.status}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#222530] transition-colors" data-testid={`admin-member-menu-${m.username}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#111319] border-[#222530] text-white">
                          <DropdownMenuItem onClick={() => setViewDetail({ memberId: m.id })} data-testid={`admin-member-view-${m.username}`} className="focus:bg-[#222530] focus:text-white cursor-pointer">
                            <Eye className="h-3.5 w-3.5 mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setModalError(""); setEditModal({ id: m.id, full_name: m.full_name, email: m.email, username: m.username, phone: m.phone || "", country: m.country || "" }); }} data-testid={`admin-member-edit-${m.username}`} className="focus:bg-[#222530] focus:text-white cursor-pointer">
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openManage(m)} data-testid={`admin-member-manage-${m.username}`} className="focus:bg-[#222530] focus:text-white cursor-pointer">
                            <SlidersHorizontal className="h-3.5 w-3.5 mr-2" /> Manage
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(m)} data-testid={`admin-member-toggle-${m.username}`} className="focus:bg-[#222530] focus:text-white cursor-pointer">
                            {m.status === "active" ? "Suspend" : "Activate"}
                          </DropdownMenuItem>
                          {!m.is_demo && (
                            <>
                              <DropdownMenuSeparator className="bg-[#222530]" />
                              <DropdownMenuItem onClick={() => openPurgeMember(m)} data-testid={`admin-member-delete-${m.username}`} className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer">
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-[#222530]" data-testid="members-pagination">
            <button onClick={() => gotoPage(page - 1)} disabled={page <= 1 || loading} className="btn-outline-gold px-3 py-1.5 text-xs disabled:opacity-40" data-testid="members-prev">Previous</button>
            <span className="text-xs text-[#64748B]" data-testid="members-page">Page {page} of {pageCount}</span>
            <button onClick={() => gotoPage(page + 1)} disabled={page >= pageCount || loading} className="btn-outline-gold px-3 py-1.5 text-xs disabled:opacity-40" data-testid="members-next">Next</button>
          </div>
        )}
      </div>

      {addProgressMember && (
        <AddProgressModal
          member={addProgressMember}
          onClose={() => setAddProgressMember(null)}
          onApplied={() => loadMembers(page, search, broker, ibId)}
        />
      )}

      {viewDetail && (
        <MemberDetailModal
          memberId={viewDetail.memberId}
          onClose={() => setViewDetail(null)}
          onChanged={(updated) => patchMember(updated.id, updated)}
        />
      )}

      {purgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="delete-member-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => !purgeModal.busy && setPurgeModal(null)} />
          <div className="card-lux relative w-full max-w-md p-6 sm:p-8 max-h-[90vh] overflow-y-auto border-red-500/30">
            <p className="eyebrow-label mb-2 text-red-400">Danger Zone</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-3">DELETE MEMBER</h3>
            <div className="text-sm space-y-1 mb-4" data-testid="delete-member-info">
              <p className="text-white font-medium">{purgeModal.member.full_name}</p>
              <p className="font-mono text-[#D4AF37] text-xs">{purgeModal.member.uid}</p>
              <p className="text-[#64748B] text-xs">{purgeModal.member.email}</p>
            </div>
            <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/25 rounded-lg px-4 py-3 mb-4" data-testid="delete-member-warning">
              This action will permanently delete this member and all associated member data. This cannot be undone.
            </p>
            {purgeModal.preview && (
              <div className="grid grid-cols-2 gap-2 text-xs max-h-40 overflow-y-auto mb-4" data-testid="delete-member-counts">
                {Object.entries(purgeModal.preview.counts).filter(([, v]) => v > 0).map(([k, v]) => (
                  <div key={k} className="flex justify-between border border-[#222530] rounded-lg px-3 py-1.5">
                    <span className="text-[#64748B] font-mono">{k}</span><span className="text-white font-mono">{v}</span>
                  </div>
                ))}
                {Object.values(purgeModal.preview.counts).every((v) => v === 0) && (
                  <p className="col-span-2 text-[#64748B] text-xs">No associated records — account only.</p>
                )}
              </div>
            )}
            <label className="text-xs text-[#64748B] tracking-wide">Type the member UID <span className="font-mono text-red-400">{purgeModal.member.uid}</span> to confirm</label>
            <input value={purgeModal.confirm} onChange={(e) => setPurgeModal((m) => ({ ...m, confirm: e.target.value }))}
              className="mt-2 mb-4 w-full bg-[#0B0C10] border border-red-500/30 rounded-lg px-3 py-2.5 text-sm text-white focus:border-red-500/60 outline-none font-mono" data-testid="delete-member-confirm-input" />
            <div className="flex gap-3">
              <button onClick={() => setPurgeModal(null)} disabled={purgeModal.busy} className="btn-outline-gold flex-1 py-2.5 text-sm disabled:opacity-50" data-testid="delete-member-cancel">Cancel</button>
              <button onClick={executeMemberPurge} disabled={purgeModal.busy || purgeModal.confirm !== purgeModal.member.uid}
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="delete-member-execute">
                {purgeModal.busy ? "Deleting..." : "Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="edit-member-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditModal(null)} />
          <div className="card-lux relative w-full max-w-md p-8">
            <p className="eyebrow-label mb-2">Edit Member</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">Member Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Username (numbers only)</label>
                <input className="input-lux font-mono" value={editModal.username} onChange={(e) => setEditModal((m) => ({ ...m, username: e.target.value.replace(/\D/g, "") }))} data-testid="edit-member-username-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Name</label>
                <input className="input-lux" value={editModal.full_name} onChange={(e) => setEditModal((m) => ({ ...m, full_name: e.target.value }))} data-testid="edit-member-fullname-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Email</label>
                <input type="email" className="input-lux" value={editModal.email} onChange={(e) => setEditModal((m) => ({ ...m, email: e.target.value }))} data-testid="edit-member-email-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Phone Number</label>
                <input className="input-lux" value={editModal.phone} onChange={(e) => setEditModal((m) => ({ ...m, phone: e.target.value }))} data-testid="edit-member-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Country</label>
                <input className="input-lux" value={editModal.country} onChange={(e) => setEditModal((m) => ({ ...m, country: e.target.value }))} data-testid="edit-member-country-input" />
              </div>
              {modalError && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="edit-member-error">{modalError}</p>}
              <div className="flex gap-3 pt-2">
                <button onClick={saveEditMember} disabled={modalBusy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="edit-member-save-button">{modalBusy ? "Saving..." : "Save"}</button>
                <button onClick={() => setEditModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="edit-member-cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {manage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="manage-member-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setManage(null)} />
          <div className="card-lux relative w-full max-w-md p-8 max-h-[90vh] overflow-y-auto">
            <p className="eyebrow-label mb-2">Manage Member</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-1">{manage.member.full_name}</h3>
            <p className="text-xs text-[#64748B] font-mono mb-6">{manage.member.uid} · {manage.member.email}</p>
            {manage.loading ? <p className="text-sm text-[#64748B]">Loading…</p> : manage.info && (
            <div className="space-y-6">
              <div data-testid="manage-ib-section">
                <label className="block text-xs font-medium text-[#94A3B8] mb-1 tracking-wide">Change Sponsoring IB</label>
                <p className="text-sm text-white mb-2" data-testid="manage-current-ib">Current: {manage.info.sponsor_name ? `${manage.info.sponsor_name} (${manage.info.sponsor_code})` : "No sponsoring IB"}</p>
                {manage.ibPick ? (
                  <div className="flex items-center justify-between rounded-lg border border-[#D4AF37]/40 px-3 py-2 text-sm text-white" data-testid="manage-ib-selected">
                    <span>{manage.ibPick.full_name} ({manage.ibPick.referral_code})</span>
                    <button onClick={() => setManage((m) => ({ ...m, ibPick: null }))} className="text-xs text-[#94A3B8]">Change</button>
                  </div>
                ) : (
                  <>
                    <input value={manage.ibQuery} onChange={(e) => setManage((m) => ({ ...m, ibQuery: e.target.value }))} placeholder="Search IB by name or referral code" className="input-lux" data-testid="manage-ib-search" />
                    {manage.ibQuery.trim().length >= 2 && (
                      <div className="mt-1 rounded-lg border border-[#2a2d38] divide-y divide-[#222530] max-h-40 overflow-y-auto">
                        {(manage.ibs || []).filter((ib) => `${ib.full_name} ${ib.referral_code} ${ib.uid}`.toLowerCase().includes(manage.ibQuery.trim().toLowerCase())).slice(0, 8).map((ib) => (
                          <button key={ib.id} onClick={() => setManage((m) => ({ ...m, ibPick: ib }))} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#D4AF37]/10" data-testid={`manage-ib-option-${ib.referral_code}`}>
                            {ib.full_name} <span className="text-xs text-[#64748B]">· {ib.referral_code} · {ib.client_count} clients</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <button onClick={() => applySponsorship({ sponsor_ib_id: manage.ibPick.id }, "IB change")} disabled={modalBusy || !manage.ibPick} className="btn-gold w-full py-2.5 text-sm mt-3 disabled:opacity-50" data-testid="manage-apply-ib-button">{modalBusy ? "Applying..." : "Apply IB Change"}</button>
              </div>
              <div className="border-t border-[#222530]" />
              <div data-testid="manage-broker-section">
                <label className="block text-xs font-medium text-[#94A3B8] mb-1 tracking-wide">Change Broker</label>
                <p className="text-sm text-white mb-2" data-testid="manage-current-broker">Current: {manage.info.registered_broker || "—"}</p>
                <select value={manage.brokerPick} onChange={(e) => setManage((m) => ({ ...m, brokerPick: e.target.value }))} className="input-lux" data-testid="manage-broker-select">
                  <option value="">Select broker</option>
                  {(manage.brokers || []).map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <button onClick={() => applySponsorship({ registered_broker: manage.brokerPick }, "Broker change")} disabled={modalBusy || !manage.brokerPick || manage.brokerPick === manage.info.registered_broker} className="btn-gold w-full py-2.5 text-sm mt-3 disabled:opacity-50" data-testid="manage-apply-broker-button">{modalBusy ? "Applying..." : "Apply Broker Change"}</button>
              </div>
              {manage.msg && <p className="text-sm text-emerald-400 bg-emerald-400/10 border border-emerald-400/25 rounded-lg px-4 py-3" data-testid="manage-member-success">{manage.msg}</p>}
              {modalError && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="manage-member-error">{modalError}</p>}
              <button onClick={() => setManage(null)} className="btn-outline-gold w-full py-2.5 text-sm" data-testid="manage-member-cancel-button">Close</button>
            </div>
            )}
            {!manage.loading && !manage.info && modalError && <p className="text-sm text-red-400" data-testid="manage-member-error">{modalError}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
